import React from 'react';
import { Table } from 'reactstrap';
import { CaretDown, CaretDownFill, CaretUpFill } from 'react-bootstrap-icons';
import { QSightPagination } from '@qsightnpm/qsight-component-library';

export interface HeaderItem<J> {
  data: React.ReactNode;
  dataRef: keyof J;
  sortKey?: string;
  sortable?: boolean;
  toolTipText?: string;
  width?: number | string;
}

export interface RowItem {
  id?: string | number;
  extraInfo?: React.ReactNode | (() => React.ReactNode);
  [key: string]: React.ReactNode | (() => React.ReactNode) | undefined;
}

export interface ReportState {
  loading: boolean;
  sortKey?: string;
  sortOrder?: 'ASC' | 'DESC';
  setSortKey: (key?: string) => void;
  setSortOrder: (order: 'ASC' | 'DESC') => void;
  pageIndex: number; // zero-based
  pageSize: number;
  totalCount: number;
  setPageSize: (size: number) => void;
  setPageIndex: (index: number) => void;
}

interface QSightDataTableProps<J> {
  headers: HeaderItem<J>[];
  cssClass?: string;
  rows: RowItem[];
  reportState: ReportState;
}

const AutoParTable = <J extends Record<string, any>>({
  headers,
  cssClass = '',
  rows,
  reportState
}: QSightDataTableProps<J>) => {
  const totalPages = Math.max(1, Math.ceil(reportState.totalCount / reportState.pageSize));
  const pageSizes = [20, 50, 100];
  const visiblePages = Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1);

  return (
    <div
      className={`table-responsive qsight-data-table--wrapper d-flex flex-column card card-default card-table ${reportState.loading ? 'loading' : 'has-loaded'}`}
    >
      <Table className={`table qsight-data-table table-sm ${cssClass}`}>
        <thead>
          <tr>
            {headers.map((header: HeaderItem<J>, index: number) => {
              const { sortable = true, sortKey } = header;

              let SortIcon: React.ComponentType<any> = CaretDown;
              if (sortKey && sortKey === reportState?.sortKey) {
                SortIcon = reportState?.sortOrder === 'ASC' ? CaretUpFill : CaretDownFill;
              }

              return (
                <th
                  key={index}
                  data-toggle='toolTip'
                  title={header?.toolTipText as string}
                  aria-sort={
                    sortKey && sortKey === reportState.sortKey ? (reportState?.sortOrder === 'ASC' ? 'ascending' : 'descending') : 'ascending'
                  }
                  style={header.width ? { width: header.width } : undefined}
                >
                  {header.data}
                  {sortable && sortKey ? (
                    <span
                      role='button'
                      onClick={() => {
                        reportState.setSortKey(sortKey);
                        reportState.setSortOrder(
                          reportState.sortKey === sortKey && reportState.sortOrder === 'ASC'
                            ? 'DESC'
                            : 'ASC'
                        );
                      }}
                    >
                      <SortIcon />
                    </span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className='table-body'>
          {rows !== null && rows.length > 0 ? (
            rows.map((row: RowItem, rowIndex: number) => {
              const ExtraInfo = row['extraInfo'] as React.ReactNode | (() => React.ReactNode);
              return (
                <React.Fragment key={(row.id as any) ?? rowIndex}>
                  <tr className={rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}>
                    {headers.map((headerItem: HeaderItem<J>, headerItemIndex: number) => {
                      const CellComponent = row[headerItem.dataRef as string] as React.ReactNode | (() => React.ReactNode);
                      return (
                        <td className='text-left' key={headerItemIndex}>
                          {React.isValidElement(CellComponent) ? (
                            CellComponent
                          ) : typeof CellComponent === 'function' ? (
                            React.createElement(CellComponent as any)
                          ) : (
                            CellComponent
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {ExtraInfo && (
                    React.isValidElement(ExtraInfo)
                      ? ExtraInfo
                      : typeof ExtraInfo === 'function'
                        ? React.createElement(ExtraInfo as any)
                        : null
                  )}
                </React.Fragment>
              );
            })
          ) : (
            !reportState.loading && (
              <tr>
                <td colSpan={headers.length} className='text-center py-4'>
                  <div className='mb-0'>
                    No results found based on the current filters applied
                  </div>
                </td>
              </tr>
            )
          )}
        </tbody>
      </Table>

      {rows && rows.length > 0 && (
        <QSightPagination
          visiblePages={visiblePages}
          pageIndex={reportState.pageIndex}
          pageSize={reportState.pageSize}
          pageSizes={pageSizes}
          totalPages={totalPages}
          totalCount={reportState.totalCount}
          setPageSize={(size) => reportState.setPageSize(size)}
          previousPage={() => reportState.setPageIndex(Math.max(0, reportState.pageIndex - 1))}
          nextPage={() => reportState.setPageIndex(Math.min(totalPages - 1, reportState.pageIndex + 1))}
          specificPage={(value) => reportState.setPageIndex(Math.max(0, Math.min(totalPages - 1, value - 1)))}
          firstPage={() => reportState.setPageIndex(0)}
          lastPage={() => reportState.setPageIndex(totalPages - 1)}
          hasBulkDropdownSelection={false}
        />
      )}

      {reportState.loading && (
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Loading...</span>
        </div>
      )}
    </div>
  );
};

export default AutoParTable;
